<template>
    <div class="container-fluid px-4">
        <BaseTable :rows="persons" :columns="columns" searchable :selectable="false" :loading="loading"
            :pagination="pagination" @changePage="onChangePage" @updatePageSize="onUpdatePageSize"
            @searchChanged="onSearchChanged">
            <template #actions="{ row }">
                <MaterialButton v-if="!readOnly" size="sm" variant="text" @click="assignSkill(row)">
                    <i class="material-icons text-lg text-success me-1">task</i>
                </MaterialButton>
            </template>
        </BaseTable>
    </div>
</template>

<script setup>
import { useStore } from "vuex";
import { computed, onMounted, ref, watch } from "vue";
import BaseTable from "@/components/Table/Table.vue";
import MaterialButton from "@/components/common/MaterialButton.vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toast-notification";

const props = defineProps({
    skillId: {
        type: [Number, String],
        default: null,
    },
    locationId: {
        type: [Number, String],
        default: null,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
});

const store = useStore();
const route = useRoute();
const toast = useToast();
const loading = ref(true);
const searchTerm = ref("");

const persons = computed(() => store.getters.interestedPersons);
const pagination = computed(() => store.getters.skillLevelPagination);

// Table columns
const columns = [
    { key: "personid", label: "Person ID" },
    { key: "firstname", label: "First Name" },
    { key: "lastname", label: "Last Name" },
    { key: "email", label: "E-mail" },
];

// For editing
const editForm = ref({
    id: null,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    status: "active",
    memberSince: ""
});

// Use props if provided, otherwise fallback to route params
const locationId = computed(() => {
    return props.locationId !== null ? Number(props.locationId) : Number(route.params.locationId);
});

const skillId = computed(() => {
    return props.skillId !== null ? Number(props.skillId) : Number(route.params.skillid);
});

const fetchNonInterestedSkills = async (page = 1, pageSize = pagination.value.pageSize || 10) => {
    if (!skillId.value) return;
    
    loading.value = true;
    try {
        await store.dispatch("getInterestedPersons", {
            skillId: skillId.value,
            locationId: locationId.value,
            pageNumber: page,
            pageSize: pageSize,
        });
    }
    finally {
        loading.value = false;
    }
}

const assignSkill = async (row) => {
    editForm.value = { ...row };

    var result = await store.dispatch("savePersonSkill", {
        personId: editForm.value.personid,
        skillId: skillId.value,
        locationId: locationId.value,
    });

    if (result) {
        toast.success(result.message, "top-right");
        fetchNonInterestedSkills()
    }
};

// Pagination Handlers
const onChangePage = async (page) => await fetchNonInterestedSkills(page);
const onUpdatePageSize = async (size) => await fetchNonInterestedSkills(1, size);
const onSearchChanged = async (term) => {
    searchTerm.value = term;
    await fetchNonInterestedSkills(1);
};

onMounted(() => fetchNonInterestedSkills());

// Watch for skillId changes and reload data
watch(() => skillId.value, () => {
    fetchNonInterestedSkills();
});

</script>